import React, {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import sv from "../../texts/sv.json";
import fi from "../../texts/fi.json";
import {isSwedenContext} from "../../utils/countryContext";
import logo from "../../images/mobile_logo2.png";
import "./migrationPage.css";

/** Marketing page describing what changed in the new version. */
const UPDATE_INFO_URL = "https://www.vikaaria.com/update";

/** The new application users should move over to. */
const NEW_APP_URL = "https://app.vikaaria.com";

const TEXTS = {fi, sv};

/** Label for each option, written in the language it selects. */
const LANGUAGE_LABELS = {fi: "Suomeksi", sv: "På svenska"};

/** Shared with the rest of the old app, which stored "fi" / "sv" here. */
const LANGUAGE_COOKIE = "_lang";

/** A year: the choice only has to outlive the migration period. */
const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Is this the Swedish deployment?
 *
 * Decided by hostname rather than by the build's country code, because the
 * country code is a build time environment variable that has to be set
 * correctly per deployment, and the copy of `.env` in the repo defaults it to
 * `sv`. The hostname is the thing the user actually typed. `runtime-config.js`
 * already picks the market the same way.
 *
 * Staging hosts (`*.ondigitalocean.app`) have neither TLD, so those fall back
 * to the build's country code.
 */
const isSwedishDeployment = () => {
    const host = window.location.hostname.toLowerCase();

    if (host.endsWith(".se")) {
        return true;
    }
    if (host.endsWith(".fi")) {
        return false;
    }

    return isSwedenContext();
};

/** Reads a cookie without pulling in js-cookie, which the frontend package does not depend on. */
const readCookie = (name) => {
    const match = document.cookie.match(
        new RegExp("(?:^|; )" + name + "=([^;]*)")
    );

    if (!match) {
        return undefined;
    }

    // The old app wrote this with js-cookie, which quotes some values.
    return decodeURIComponent(match[1]).replace(/^"|"$/g, "");
};

/**
 * The only thing this deployment renders any more.
 *
 * Vikaaria has moved, so App mounts this instead of the router. There is no
 * route to anywhere else, no login and no way to dismiss it: the two links out
 * are the whole page.
 *
 * Language:
 *  - The Swedish deployment is Swedish only and shows no selector.
 *  - The Finnish deployment defaults to Finnish but offers both, because
 *    vikaaria.fi has always served Swedish speaking Finns too. Order of
 *    preference there is an explicit choice made on this page, then the
 *    `siteLang` on the logged in user's token (1 = fi, 2 = sv), then Finnish.
 */
const MigrationPage = () => {
    const siteLang = useSelector((state) => state?.auth?.teacher?.siteLang);
    const swedishOnly = isSwedishDeployment();

    const [languageCode, setLanguageCode] = useState(() => {
        if (swedishOnly) {
            return "sv";
        }

        const stored = readCookie(LANGUAGE_COOKIE);
        if (stored === "fi" || stored === "sv") {
            return stored;
        }

        if (siteLang === 2) {
            return "sv";
        }
        if (siteLang === 1) {
            return "fi";
        }

        return "fi";
    });

    const language = TEXTS[languageCode];

    const chooseLanguage = useCallback((code) => {
        setLanguageCode(code);
        document.cookie =
            `${LANGUAGE_COOKIE}=${code}; path=/; max-age=${LANGUAGE_COOKIE_MAX_AGE}; samesite=lax`;
    }, []);

    // Keeps assistive tech and the browser's own text handling in step with the
    // language actually on screen.
    useEffect(() => {
        document.documentElement.lang = languageCode;
    }, [languageCode]);

    return (
        <div className="migration-page">
            <main className="migration-page__content">
                {!swedishOnly && (
                    <div
                        className="migration-page__languages"
                        role="group"
                        aria-label="Kieli / Språk"
                    >
                        {["fi", "sv"].map((code) => (
                            <button
                                key={code}
                                type="button"
                                lang={code}
                                className={
                                    "migration-page__language" +
                                    (code === languageCode
                                        ? " migration-page__language--active"
                                        : "")
                                }
                                aria-pressed={code === languageCode}
                                onClick={() => chooseLanguage(code)}
                            >
                                {LANGUAGE_LABELS[code]}
                            </button>
                        ))}
                    </div>
                )}

                <img
                    src={logo}
                    alt="Vikaaria"
                    className="migration-page__logo"
                />

                <h1 className="migration-page__title">
                    {language.migration_title}
                </h1>

                {/* The body text is multi paragraph: newlines in the
                    localisation string become separate paragraphs. */}
                <div className="migration-page__body">
                    {language.migration_body.split("\n").map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                    ))}
                </div>

                <div className="migration-page__actions">
                    {/* Same tab: moving over to the new app is the point. */}
                    <a
                        href={NEW_APP_URL}
                        className="migration-page__button migration-page__button--filled"
                    >
                        {language.migration_cta_app}
                    </a>
                    <a
                        href={UPDATE_INFO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="migration-page__button migration-page__button--outline"
                    >
                        {language.migration_cta_info}
                    </a>
                </div>
            </main>
        </div>
    );
};

export default MigrationPage;
